// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ButtonUI, CustomThemeProvider, ModalUI, TableListV2, Toast, usePortal } from 'sonata-react-components';

import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';

import './style.scss';

export type CampaignItem = {
  id: string;
  name: string;
} & Record<string, any>;

type Props = {
  data: Record<string, any>[];
  isModalOpened: boolean;
  onClose: () => void;
  onSaveCampaignsSelected: (selectedCampaigns: CampaignItem[]) => void;
};

function EditSelectionModal({ data, isModalOpened, onClose, onSaveCampaignsSelected }: Props) {
  const { formatMessage } = useIntl();
  const modalRef: any = useRef();
  const hasMount: any = useRef(false);
  const dataCampaigns: CampaignItem[] = useSelector(getSelectedCampaigns);

  useEffect(() => {
    isModalOpened
      ? modalRef?.current && modalRef.current?.openModal()
      : hasMount.current && modalRef?.current && modalRef.current.closeModal();
  }, [isModalOpened]);

  const [{ prevSelectedCampaigns, selectedCampaigns, showToast }, setState] = useState({
    prevSelectedCampaigns: data,
    selectedCampaigns: data,
    showToast: false
  });

  const onChangeSelection = (items: Record<string, any>[]) => {
    setState(prevState => ({
      ...prevState,
      selectedCampaigns: items,
      showToast: items.length === 0
    }));
  };

  const onCloseToast = () => {
    setState(prevState => ({
      ...prevState,
      showToast: false
    }));
  };

  const onSaveCampaigns = useCallback(() => {
    onSaveCampaignsSelected(selectedCampaigns as CampaignItem[]);
    setState(prevState => ({
      ...prevState,
      prevSelectedCampaigns: selectedCampaigns
    }));
    modalRef.current.closeModal();
  }, [selectedCampaigns]);

  const onCloseModal = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      selectedCampaigns: prevSelectedCampaigns
    }));
    onClose();
  }, [prevSelectedCampaigns]);

  const buttonDisabled = useMemo(() => {
    return selectedCampaigns.length < 1;
  }, [selectedCampaigns]);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name'
      }
    ],
    []
  );

  const defaultSelectedRows = useMemo(() => {
    return dataCampaigns.reduce(
      (acc, campaign) => ({
        ...acc,
        [campaign.id]: selectedCampaigns.map(e => e.id).includes(campaign.id)
      }),
      {} as Record<number, boolean>
    );
  }, []);

  const targetBody = usePortal('editSelectionModal');
  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <ModalUI
        centerButton={
          <ButtonUI
            disabled={buttonDisabled}
            onClick={onSaveCampaigns}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Save changes',
              id: 'reporting.header.multi.modal.saveButton'
            })}
            type="primary"
          />
        }
        closeModalCallback={onCloseModal}
        description={formatMessage({
          defaultMessage: 'Choose which lines item you want to upload your report on',
          id: 'reporting.header.multi.modal.description'
        })}
        ref={modalRef}
        title={formatMessage({
          defaultMessage: 'Line item selection',
          id: 'reporting.header.multi.modal.title'
        })}
      >
        {showToast && (
          <Toast
            autoClose={false}
            className="editSelectionModal__toast"
            closeButton
            closeToast={onCloseToast}
            icon="fal fa-exclamation-triangle"
            text={formatMessage({
              defaultMessage: 'You need to mark a minimum of one campaign lines to save the changes.',
              id: 'reporting.header.multi.modal.toast'
            })}
            type="warning"
          />
        )}
        <TableListV2
          columns={columns}
          config={{
            globalFilter: {
              position: 'center',
              show: true
            },
            itemsSelectedConfig: {
              enable: true,
              label: formatMessage({
                defaultMessage: 'Campaign lines selected',
                id: 'reporting.campaignListSelector.counterLabel'
              })
            },
            maxHeight: 472,
            onChangeSelection,
            toolbar: {
              showSelectAll: true
            },
            type: 'list'
          }}
          data={dataCampaigns}
          defaultSelectedRows={defaultSelectedRows}
          id="campaignListSelector-editSelectionModal"
        />
      </ModalUI>
    </CustomThemeProvider>,
    targetBody
  );
}

export default React.memo(EditSelectionModal);
